import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, MapPin, MountainSnow, Flower,
  Plane, TreePine, Sparkles, Lamp, Church, Check, Loader2, MessageCircle,
  Clock, Bed, Car, PlaneTakeoff, Bus, CloudSun, Phone,
} from "lucide-react";
import { getPortal } from "@/lib/portals";
import { BRAND, whatsappLink } from "@/lib/brand";
import { submitEnquiry } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const ICONS = { temple: Church, flower: Flower, om: Sparkles, lamp: Lamp, plane: Plane, mountain: MountainSnow, trees: TreePine, car: Car, van: Bus };

export default function Book({ portalKey = "stay" }) {
  const portal = useMemo(() => getPortal(portalKey), [portalKey]);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const catId = params.get("c") || params.get("d");
  const optId = params.get("o") || params.get("h");
  const category = useMemo(() => portal.categories.find(c => c.id === catId), [portal, catId]);
  const option = useMemo(() => category?.options?.find(o => o.id === optId), [category, optId]);

  const isDetail = portal.mode === "detail";
  const step = isDetail
    ? (category ? 2 : 1)
    : (option ? 3 : category ? 2 : 1);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step, category?.id]);
  useEffect(() => { setParams({}, { replace: true }); /* eslint-disable-next-line */ }, [portalKey]);

  const setCat = (id) => setParams({ c: id });
  const setOpt = (id) => setParams({ c: category.id, o: id });
  const clearOpt = () => setParams({ c: category.id });
  const clearAll = () => setParams({});

  return (
    <div className="bg-kc min-h-screen" data-testid={`portal-${portal.key}`}>
      {/* ============ TOP RIBBON ============ */}
      <section className="relative pt-32 md:pt-40 pb-14 md:pb-20 bg-black-lux text-[#F8F6F2] overflow-hidden">
        <img src={category?.image || portal.heroImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,11,12,0.7), rgba(11,11,12,0.95))" }} />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-12">
          <PortalSwitch current={portal.key} />

          <nav className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-[#F8F6F2]/60 flex-wrap" data-testid="portal-breadcrumb">
            <button onClick={() => nav("/")} className="hover:text-gold transition-colors" data-testid="crumb-home">Home</button>
            <span className="opacity-40">/</span>
            <button onClick={clearAll} className={step === 1 ? "text-gold" : "hover:text-gold transition-colors"} data-testid="crumb-step1">{portal.step1CrumbLabel}</button>
            {category && (<>
              <span className="opacity-40">/</span>
              <button onClick={isDetail ? clearAll : clearOpt} className={step === 2 ? "text-gold" : "hover:text-gold transition-colors"} data-testid="crumb-step2">{category.name}</button>
            </>)}
            {option && (<>
              <span className="opacity-40">/</span>
              <span className="text-gold" data-testid="crumb-step3">{portal.step3Label}</span>
            </>)}
          </nav>

          <div className="mt-10 max-w-4xl">
            {step === 1 && (<>
              <div className="overline text-gold">{portal.overline}</div>
              <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
                {portal.step1Title} <span className="italic text-gold">{portal.step1TitleAccent}</span>
              </h1>
              <p className="mt-6 text-[15px] md:text-[16px] leading-[1.8] text-[#F8F6F2]/75 max-w-2xl">{portal.step1Sub}</p>
            </>)}
            {step === 2 && category && (<>
              <div className="overline text-gold">Step 2 · {portal.step2Label}</div>
              <h1 className="font-serif-display text-4xl md:text-6xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">{category.name}</h1>
              <p className="mt-4 text-[#F8F6F2]/70 text-[14px] tracking-wide">{category.subtitle} · {category.tagline}</p>
            </>)}
            {step === 3 && category && option && (<>
              <div className="overline text-gold">Step 3 · {portal.step3Label}</div>
              <h1 className="font-serif-display text-3xl md:text-5xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
                <span className="italic text-gold">{portal.step3IntroTitle}</span> {portal.step3IntroSub}
              </h1>
              <p className="mt-4 text-[#F8F6F2]/70 text-[14px] tracking-wide">{category.name} · {option.name}</p>
            </>)}
          </div>
        </div>
      </section>

      {step === 1 && (portal.sections ? <SectionedGrid portal={portal} onSelect={setCat} /> : <CategoryGrid portal={portal} onSelect={setCat} />)}
      {step === 2 && category && isDetail && <DetailView portal={portal} category={category} onBack={clearAll} />}
      {step === 2 && category && !isDetail && <OptionList portal={portal} category={category} onBack={clearAll} onSelect={setOpt} />}
      {step === 3 && category && option && <BookingForm portal={portal} category={category} option={option} onBack={clearOpt} />}
    </div>
  );
}

// ============================================================================
const PORTAL_TABS = [
  { k: "stay", label: "Stays", to: "/book" },
  { k: "yatra", label: "Yatra", to: "/book/yatra" },
  { k: "wedding", label: "Weddings", to: "/book/wedding" },
  { k: "anniversary", label: "Anniversary", to: "/book/anniversary" },
  { k: "event", label: "Events", to: "/book/event" },
  { k: "transport", label: "Transport", to: "/book/transport" },
];

const PortalSwitch = ({ current }) => (
  <div className="flex flex-wrap gap-1 border border-[#D4AF37]/25 p-1 self-start w-fit" data-testid="portal-switch">
    {PORTAL_TABS.map(t => (
      <Link key={t.k} to={t.to} className={`px-4 py-2 text-[10px] uppercase tracking-[0.24em] transition-colors ${current === t.k ? "bg-gold text-[#0F0F0F]" : "text-[#F8F6F2]/70 hover:text-gold"}`} data-testid={`portal-tab-${t.k}`}>
        {t.label}
      </Link>
    ))}
  </div>
);

// ============================================================================
// STEP 1 — CATEGORY GRID (flat, no sections)
// ============================================================================
const CategoryGrid = ({ portal, onSelect }) => (
  <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="portal-step1-grid">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {portal.categories.map((c, i) => <YatraCard key={c.id} portal={portal} category={c} index={i} onSelect={onSelect} />)}
    </div>
    {portal.key === "transport" && <TransportTrustStrip />}
  </section>
);

const TRUST_ITEMS = [
  { label: "Verified Drivers", note: "Police-verified · trained for hill roads" },
  { label: "GPS Tracked",       note: "Live location shared with your family" },
  { label: "Fixed Pricing",     note: "No surge, no last-minute surprises" },
  { label: "24 × 7 Support",    note: "One-tap WhatsApp · roadside assistance" },
];

const TransportTrustStrip = () => (
  <Reveal className="mt-20 md:mt-24 border-t border-kc pt-12 md:pt-16" data-testid="transport-trust">
    <div className="text-center max-w-3xl mx-auto mb-10">
      <div className="overline">Every ride, held with care</div>
      <h2 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-[1.05] tracking-[-0.02em]">
        The KC promise on <span className="italic text-gold">every kilometre.</span>
      </h2>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
      {TRUST_ITEMS.map((t, i) => (
        <div key={i} className="border border-kc p-6 md:p-8 text-center bg-surface" data-testid={`trust-${i}`}>
          <div className="text-[10px] uppercase tracking-[0.28em] text-gold">№ {String(i + 1).padStart(2, "0")}</div>
          <div className="font-serif-display text-lg md:text-xl mt-4 leading-tight">{t.label}</div>
          <div className="mt-3 text-[12px] text-ink-mute leading-[1.6]">{t.note}</div>
        </div>
      ))}
    </div>
  </Reveal>
);

// STEP 1 — SECTIONED GRID (grouped)
const SectionedGrid = ({ portal, onSelect }) => (
  <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-24 space-y-24" data-testid="portal-step1-sectioned">
    {portal.sections.map((s, si) => {
      const cats = portal.categories.filter(c => c.section === s.key);
      if (cats.length === 0) return null;
      return (
        <div key={s.key} data-testid={`section-${s.key}`}>
          <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-10">
            <div className="md:col-span-7">
              <div className="overline">Chapter {String(si + 1).padStart(2, "0")}</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-4 font-light leading-[1.02] tracking-[-0.02em]">
                {s.label}
              </h2>
            </div>
            <p className="md:col-span-5 text-ink-soft text-[14.5px] leading-[1.75]">{s.sub}</p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cats.map((c, i) => <YatraCard key={c.id} portal={portal} category={c} index={i} onSelect={onSelect} />)}
          </div>
        </div>
      );
    })}
  </section>
);

// ============================================================================
// YATRA CARD — used for both flat + sectioned grids
// ============================================================================
const YatraCard = ({ portal, category, index, onSelect }) => {
  const Icon = ICONS[category.icon] || MapPin;
  const c = category;
  return (
    <Reveal delay={index * 60}>
      <div className="group relative overflow-hidden bg-black-lux border border-kc hover:border-gold/60 transition-colors flex flex-col" data-testid={`portal-cat-${c.id}`}>
        {/* Image */}
        <button onClick={() => onSelect(c.id)} className="relative h-[280px] md:h-[320px] overflow-hidden text-left" data-testid={`portal-cat-image-${c.id}`}>
          <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,11,12,0.05) 0%, rgba(11,11,12,0.75) 100%)" }} />
          <div className="absolute top-5 left-5">
            <div className="w-11 h-11 bg-black-lux/80 backdrop-blur border border-gold/40 flex items-center justify-center text-gold">
              <Icon size={18} strokeWidth={1.4} />
            </div>
          </div>
          <div className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.28em] text-[#F8F6F2]/80 border border-[#F8F6F2]/30 px-3 py-1 backdrop-blur">
            {String(index + 1).padStart(2, "0")}
          </div>
          {c.seasonalRibbon && (
            <div className="absolute top-16 right-5 text-[9px] uppercase tracking-[0.28em] bg-gold text-[#0F0F0F] px-3 py-1.5 font-medium">
              {c.seasonalRibbon}
            </div>
          )}
          <div className="absolute bottom-5 left-5 right-5 text-[#F8F6F2]">
            <div className="font-serif-display text-2xl md:text-3xl font-light leading-tight">{c.name}</div>
            <div className="text-[11px] uppercase tracking-[0.24em] mt-2 text-gold/90">{c.tagline}</div>
          </div>
        </button>

        {/* Body */}
        <div className="p-6 md:p-7 flex flex-col flex-1 bg-black-lux text-[#F8F6F2]">
          {c.subtitle && <div className="text-[11px] uppercase tracking-[0.24em] text-[#F8F6F2]/50">{c.subtitle}</div>}

          {portal.mode === "detail" && c.duration && (
            <div className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold">
              <Clock size={12} /> {c.duration}
            </div>
          )}

          {portal.mode === "detail" && c.includes && c.includes.length > 0 && (
            <div className="mt-4 text-[12px] text-[#F8F6F2]/60 leading-[1.6] line-clamp-2">
              Includes: {c.includes.slice(0, 4).join(" · ")}
              {c.includes.length > 4 && ` +${c.includes.length - 4} more`}
            </div>
          )}

          {/* Event-portal signature journey: route + duration */}
          {portal.key === "event" && c.duration && (
            <div className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold">
              <Clock size={12} /> {c.duration}
            </div>
          )}
          {portal.key === "event" && c.route && (
            <div className="mt-3 text-[11.5px] text-[#F8F6F2]/60 leading-[1.7] font-medium tracking-wide">
              {c.route}
            </div>
          )}
          {portal.key === "event" && c.description && (
            <div className="mt-3 text-[12.5px] text-[#F8F6F2]/60 leading-[1.7] line-clamp-3">
              {c.description}
            </div>
          )}

          {/* Transport-only: capacity + features */}
          {portal.key === "transport" && c.capacity && (
            <div className="mt-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold">
              <MapPin size={12} /> {c.capacity}
            </div>
          )}
          {portal.key === "transport" && c.features && c.features.length > 0 && (
            <ul className="mt-4 space-y-1.5">
              {c.features.slice(0, 3).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] text-[#F8F6F2]/65 leading-relaxed">
                  <span className="mt-1.5 w-1 h-1 bg-gold flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
          )}
          {portal.key === "transport" && c.startingFrom && (
            <div className="mt-5 pt-4 border-t border-[#D4AF37]/25 flex items-baseline justify-between gap-2">
              <span className="text-[10px] uppercase tracking-[0.24em] text-[#F8F6F2]/50">{c.isHelicopter ? "Rate" : "Starting From"}</span>
              <span className="font-serif-display text-lg text-gold">{c.startingFrom}</span>
            </div>
          )}

          {/* Dual CTA */}
          <div className="mt-6 pt-5 border-t border-[#D4AF37]/25 flex gap-2">
            <button
              onClick={() => onSelect(c.id)}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-[10px] uppercase tracking-[0.24em] border border-gold/40 text-gold hover:bg-gold hover:text-[#0F0F0F] transition-colors"
              data-testid={`portal-cat-explore-${c.id}`}
            >
              Explore <ArrowUpRight size={12} />
            </button>
            <button
              onClick={() => { onSelect(c.id); setTimeout(() => document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" }), 500); }}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-[10px] uppercase tracking-[0.24em] bg-gold text-[#0F0F0F] hover:bg-[#F8F6F2] transition-colors"
              data-testid={`portal-cat-book-${c.id}`}
            >
              {portal.key === "transport" ? (c.isHelicopter ? "Enquire Charter" : "Get Quote") : "Book Now"}
            </button>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

// ============================================================================
// STEP 2 — OPTION LIST (options mode)
// ============================================================================
const OptionList = ({ portal, category, onBack, onSelect }) => (
  <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="portal-step2-list">
    <button onClick={onBack} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-ink-soft hover:text-gold transition-colors mb-10" data-testid="back-to-step1">
      <ArrowLeft size={13} /> Back to {portal.step1CrumbLabel}
    </button>

    {portal.key === "event" && (category.includes || category.whyChoose) && (
      <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 mb-14 md:mb-16" data-testid="event-brief">
        {category.includes && (
          <div className="lg:col-span-7">
            <div className="overline">Package Includes</div>
            <h3 className="font-serif-display text-2xl md:text-3xl mt-4 font-light leading-tight">
              Everything that makes <span className="italic text-gold">this experience.</span>
            </h3>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {category.includes.map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-[13.5px] leading-[1.6] text-ink" data-testid={`event-inc-${i}`}>
                  <Check size={13} className="text-gold flex-shrink-0 mt-1" strokeWidth={2} /> {it}
                </li>
              ))}
            </ul>
          </div>
        )}
        {category.whyChoose && (
          <aside className="lg:col-span-5 bg-black-lux text-[#F8F6F2] p-8 md:p-10 border border-gold/25" data-testid="event-why">
            <div className="overline no-line text-gold">Why Choose This Experience?</div>
            <h3 className="font-serif-display text-2xl md:text-[26px] mt-4 leading-tight">
              Held with the <span className="italic text-gold">KC promise.</span>
            </h3>
            <ul className="mt-8 space-y-4">
              {category.whyChoose.map((w, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`event-why-${i}`}>
                  <span className="text-[10px] uppercase tracking-[0.24em] text-gold pt-1 flex-shrink-0">№ {String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[13px] leading-[1.65] text-[#F8F6F2]/85">{w}</span>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </Reveal>
    )}

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
      {category.options.map((o, i) => (
        <Reveal key={o.id} delay={i * 40}>
          <button onClick={() => onSelect(o.id)} className={`group w-full text-left p-6 md:p-8 border transition-all ${o.featured ? "bg-black-lux text-[#F8F6F2] border-gold/60 hover:border-gold" : "bg-surface border-kc hover:border-gold/50 hover:bg-cream"}`} data-testid={`portal-opt-${o.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <MapPin size={14} className="text-gold" strokeWidth={1.4} />
                  <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Option {String(i + 1).padStart(2, "0")}</div>
                  {o.featured && <span className="text-[9px] uppercase tracking-[0.28em] bg-gold text-[#0F0F0F] px-2 py-0.5">Signature</span>}
                </div>
                <div className="font-serif-display text-xl md:text-2xl mt-4 leading-tight font-light">{o.name}</div>
                {o.note && <div className={`mt-2 text-[12px] tracking-wide ${o.featured ? "text-[#F8F6F2]/60" : "text-ink-mute"}`}>{o.note}</div>}
              </div>
              <div className={`flex-shrink-0 w-10 h-10 border flex items-center justify-center transition-colors ${o.featured ? "border-gold/40 text-gold group-hover:bg-gold group-hover:text-[#0F0F0F]" : "border-kc text-ink group-hover:border-gold group-hover:text-gold"}`}>
                <ArrowRight size={14} />
              </div>
            </div>
          </button>
        </Reveal>
      ))}
    </div>
  </section>
);

// ============================================================================
// STEP 2 — DETAIL VIEW (detail mode · used for Yatra portal)
// ============================================================================
const DetailView = ({ portal, category, onBack }) => (
  <>
    {/* ---------- Highlights ---------- */}
    <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-20" data-testid="detail-highlights">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-ink-soft hover:text-gold transition-colors mb-10" data-testid="back-to-yatras">
        <ArrowLeft size={13} /> Back to {portal.step1CrumbLabel}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="overline">Tour Highlights</div>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-[1.02] tracking-[-0.02em]">
            {category.name} <br /><span className="italic text-gold">at a glance.</span>
          </h2>
          <div className="mt-8 flex flex-wrap gap-6">
            {category.duration && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold"><Clock size={16} /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-gold">Duration</div>
                  <div className="font-serif-display text-lg mt-1">{category.duration}</div>
                </div>
              </div>
            )}
            {category.helicopter && (
              <div className="flex items-center gap-3 max-w-xs">
                <div className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold"><PlaneTakeoff size={16} /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-gold">Helicopter</div>
                  <div className="text-[12px] mt-1 text-ink-soft">{category.helicopter}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <ul className="lg:col-span-7 space-y-4">
          {category.highlights?.map((h, i) => (
            <Reveal key={i} delay={i * 60} className="flex items-start gap-4 pb-4 border-b border-kc">
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold pt-1 flex-shrink-0">№ {String(i + 1).padStart(2, "0")}</div>
              <p className="text-[15px] leading-[1.75] text-ink">{h}</p>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>

    {/* ---------- Included Destinations ---------- */}
    {category.includes?.length > 0 && (
      <section className="bg-cream section-pad" data-testid="detail-includes">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <Reveal className="mb-12">
            <div className="overline">Included Destinations</div>
            <h2 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-[1] tracking-[-0.02em]">
              The full route <span className="italic text-gold">of your yatra.</span>
            </h2>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {category.includes.map((place, i) => (
              <div key={i} className="inline-flex items-center gap-2 border border-ink/15 bg-surface px-4 py-2.5 text-[13px] hover:border-gold transition-colors" data-testid={`detail-place-${i}`}>
                <MapPin size={12} className="text-gold" />
                {place}
              </div>
            ))}
          </div>
        </div>
      </section>
    )}

    {/* ---------- Hotels + Vehicles ---------- */}
    {(category.hotels?.length > 0 || category.vehicles?.length > 0) && (
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-20" data-testid="detail-services">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {category.hotels?.length > 0 && (
            <div className="bg-surface border border-kc p-8 md:p-10">
              <Bed size={22} className="text-gold" strokeWidth={1.3} />
              <div className="overline no-line mt-6">Hotel Options</div>
              <ul className="mt-6 space-y-3">
                {category.hotels.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-[14px] text-ink leading-relaxed">
                    <span className="mt-2 w-1 h-1 bg-gold flex-shrink-0" /> {h}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {category.vehicles?.length > 0 && (
            <div className="bg-surface border border-kc p-8 md:p-10">
              <Car size={22} className="text-gold" strokeWidth={1.3} />
              <div className="overline no-line mt-6">Vehicle Options</div>
              <ul className="mt-6 space-y-3">
                {category.vehicles.map((v, i) => (
                  <li key={i} className="flex items-start gap-3 text-[14px] text-ink leading-relaxed">
                    <span className="mt-2 w-1 h-1 bg-gold flex-shrink-0" /> {v}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    )}

    {/* ---------- Inline Enquiry Form ---------- */}
    <section id="enquire" className="bg-black-lux text-[#F8F6F2] section-pad" data-testid="detail-enquire">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline text-gold">Direct Enquiry</div>
            <h3 className="font-serif-display text-3xl md:text-4xl mt-4 font-light leading-tight">
              Ready to plan <br /><span className="italic text-gold">{category.name}?</span>
            </h3>
            <p className="mt-6 text-[14px] leading-[1.8] text-[#F8F6F2]/75">
              Share a few details and our travel director will call you personally within 10 minutes. We handle everything — permits, helicopters, taxis, pandits and every warm meal along the road.
            </p>

            <div className="mt-10 pt-8 border-t border-[#D4AF37]/25 space-y-5">
              <a href={whatsappLink(`Namaste 🙏 I would like to plan the ${category.name} yatra with THE KC INDIA.`)} target="_blank" rel="noreferrer" className="btn-gold w-full justify-center" data-testid="detail-whatsapp">
                <MessageCircle size={14} /> Book on WhatsApp
              </a>
              <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="btn-ghost-light w-full justify-center" data-testid="detail-call">
                Call · {BRAND.phone}
              </a>
            </div>
          </div>

          <div className="lg:col-span-8">
            <YatraEnquiryForm portal={portal} category={category} />
          </div>
        </div>
      </div>
    </section>
  </>
);

// ============================================================================
// YATRA ENQUIRY FORM — used inline in DetailView
// ============================================================================
const YatraEnquiryForm = ({ portal, category }) => {
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const buildWhatsApp = () => {
    const rows = [
      `Namaste 🙏 I would like to plan the ${category.name} with THE KC INDIA.`,
      ``,
      `Package: ${category.name} — ${category.subtitle || ""}`,
      `Duration: ${category.duration || "TBD"}`,
      ``,
      `Name: ${form.name}`,
      `Mobile: ${form.mobile}`,
      form.email ? `Email: ${form.email}` : null,
    ];
    portal.fields.forEach(f => {
      if (form[f.k]) rows.push(`${f.label.replace(" · Optional", "")}: ${form[f.k]}`);
    });
    return whatsappLink(rows.filter(Boolean).join("\n"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { toast.error("Please share your name and mobile number."); return; }
    setBusy(true);
    try {
      const payload = {
        type: portal.enquiryType,
        name: form.name,
        mobile: form.mobile,
        email: form.email || undefined,
        destination: category.name,
        hotel: category.subtitle || "Yatra Package",
        package: category.name,
        ...Object.fromEntries(portal.fields.map(f => [f.k, form[f.k] || undefined])),
      };
      const saved = await submitEnquiry(payload);
      const url = buildWhatsApp();
      window.open(url, "_blank", "noreferrer");
      setDone({ id: saved.id, url });
      toast.success("Received. Namaste 🙏");
    } catch { toast.error("Something interrupted our line. Please WhatsApp us instead."); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div className="border border-gold/40 bg-black-lux p-10 md:p-14 text-center" data-testid="detail-success">
      <div className="w-14 h-14 mx-auto rounded-full bg-gold/10 border border-gold flex items-center justify-center text-gold"><Check size={22} strokeWidth={2} /></div>
      <div className="overline no-line mt-8 text-gold">Namaste</div>
      <h3 className="font-serif-display text-3xl mt-4 leading-tight">Your enquiry has been received.</h3>
      <p className="mt-4 text-[#F8F6F2]/70 text-[14px]">A member of our team will call you on <b>{form.mobile}</b> within 10 minutes.</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <a href={done.url} target="_blank" rel="noreferrer" className="btn-gold inline-flex" data-testid="detail-open-whatsapp">
          <MessageCircle size={14} /> Continue on WhatsApp
        </a>
        <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="btn-ghost-light inline-flex" data-testid="detail-call-now">
          <Phone size={13} /> Call Now
        </a>
      </div>
      <div className="mt-8 pt-6 border-t border-[#D4AF37]/25 text-[10px] uppercase tracking-[0.28em] text-[#F8F6F2]/40">Enquiry ID · {done.id.slice(0, 8)}</div>
    </div>
  );

  return (
    <form onSubmit={submit} className="bg-[#0F0F0F] border border-[#D4AF37]/25 p-8 md:p-12" data-testid="detail-form">
      <div className="overline no-line text-gold">Guest Details</div>
      <h3 className="font-serif-display text-3xl md:text-4xl mt-4 leading-tight">Begin Your Journey</h3>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        <FieldInput dark label="Name" value={form.name} onChange={v => upd("name", v)} placeholder="Your full name" required testid="yatra-name" />
        <FieldInput dark label="Mobile" value={form.mobile} onChange={v => upd("mobile", v)} placeholder="+91 …" required testid="yatra-mobile" />
        <FieldInput dark label="Email · Optional" value={form.email} onChange={v => upd("email", v)} placeholder="you@example.com" col={2} testid="yatra-email" />
        {portal.fields.map(f => {
          const testid = `yatra-${f.k}`;
          if (f.type === "textarea") return <FieldInput dark key={f.k} textarea label={f.label} value={form[f.k] || ""} onChange={v => upd(f.k, v)} placeholder={f.placeholder} col={f.col} testid={testid} />;
          return <FieldInput dark key={f.k} type={f.type} label={f.label} value={form[f.k] || ""} onChange={v => upd(f.k, v)} placeholder={f.placeholder} col={f.col} testid={testid} />;
        })}
      </div>
      <button type="submit" disabled={busy} className="mt-10 btn-gold w-full md:w-auto justify-center" data-testid="yatra-submit">
        {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <>{portal.submitLabel} <ArrowUpRight size={14} /></>}
      </button>
      <p className="mt-5 text-[10px] uppercase tracking-[0.24em] text-[#F8F6F2]/40">By sharing your details, you consent to be contacted by THE KC INDIA on WhatsApp, phone and email.</p>
    </form>
  );
};

// ============================================================================
// STEP 3 — BOOKING FORM (options mode)
// ============================================================================
// COMPLETE YOUR JOURNEY (Optional) · Stay-only upsell
// ============================================================================
const JOURNEY_ADDONS = [
  { key: "transfer",     label: "Transfer",     icon: "car",    sub: ["Airport Pickup · Dehradun", "Round-Trip Vehicle", "Full-Yatra Vehicle"] },
  { key: "helicopter",   label: "Helicopter",   icon: "plane",  sub: ["Kedarnath Shuttle", "Char Dham Package", "Custom Charter"] },
  { key: "meals",        label: "Meals",        icon: "bed",    sub: ["Pure Pahadi Thali", "Multi-Cuisine Fine-Dine", "Candlelight Dinner"] },
  { key: "celebration",  label: "Celebration",  icon: "flower", sub: ["Birthday", "Anniversary", "Small Ceremony"] },
  { key: "photography",  label: "Photography",  icon: "temple", sub: ["Couple Shoot", "Family Portrait", "Cinematic Reel"] },
  { key: "puja_yatra",   label: "Puja & Yatra", icon: "om",     sub: ["Rudrabhishek", "Kedarnath Darshan", "Triyuginarayan Blessing"] },
];

const JourneyAddons = ({ selections, setSelections }) => {
  const [openKey, setOpenKey] = useState(null);
  const toggle = (key, sub) => {
    const set = new Set(selections[key] || []);
    set.has(sub) ? set.delete(sub) : set.add(sub);
    setSelections({ ...selections, [key]: Array.from(set) });
  };
  const activeCount = (k) => (selections[k] || []).length;
  const AddonIcon = ({ i }) => { const I = ICONS[i] || Sparkles; return <I size={13} strokeWidth={1.4} />; };

  return (
    <div className="mt-10 pt-8 border-t border-kc" data-testid="journey-addons">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Complete Your Journey · Optional</div>
          <div className="mt-2 text-[13px] text-ink-soft">Add anything you may need — tap to expand, we'll price it in your quote.</div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
        {JOURNEY_ADDONS.map((a) => {
          const isOpen = openKey === a.key;
          const count = activeCount(a.key);
          return (
            <button
              key={a.key}
              type="button"
              onClick={() => setOpenKey(isOpen ? null : a.key)}
              className={`text-left px-4 py-3.5 border flex items-center justify-between gap-3 transition-colors ${isOpen || count > 0 ? "bg-gold text-[#0F0F0F] border-gold" : "bg-surface border-kc text-ink hover:border-gold"}`}
              data-testid={`journey-addon-${a.key}`}
            >
              <span className="inline-flex items-center gap-2.5">
                <AddonIcon i={a.icon} />
                <span className="text-[12px] uppercase tracking-[0.22em]">{a.label}</span>
              </span>
              <span className={`text-[10px] tracking-[0.22em] ${count > 0 ? "" : isOpen ? "" : "text-gold"}`}>
                {count > 0 ? `${count} added` : isOpen ? "close" : "add"}
              </span>
            </button>
          );
        })}
      </div>

      {openKey && (
        <div className="mt-4 p-5 md:p-6 bg-cream border border-kc" data-testid="journey-expanded">
          <div className="text-[10px] uppercase tracking-[0.28em] text-gold">
            {JOURNEY_ADDONS.find(x => x.key === openKey)?.label}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {JOURNEY_ADDONS.find(x => x.key === openKey)?.sub.map((s) => {
              const active = (selections[openKey] || []).includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggle(openKey, s)}
                  className={`inline-flex items-center gap-2 text-[12px] px-3.5 py-2 border transition-colors ${active ? "bg-gold text-[#0F0F0F] border-gold" : "border-kc text-ink hover:border-gold"}`}
                  data-testid={`journey-sub-${openKey}-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {active ? <Check size={11} /> : <span className="w-2.5 h-2.5 border border-current" />} {s}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
const BookingForm = ({ portal, category, option, onBack }) => {
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [journey, setJourney] = useState({});     // { transfer: [..], meals: [..] }
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const journeySummary = () => {
    const lines = JOURNEY_ADDONS
      .filter(a => (journey[a.key] || []).length > 0)
      .map(a => `${a.label}: ${journey[a.key].join(" · ")}`);
    return lines.length ? lines.join(" | ") : "";
  };

  const buildWhatsApp = () => {
    const rows = [
      `Namaste 🙏 I would like to enquire with THE KC INDIA.`, ``,
      `Portal: ${portal.overline}`,
      `📍 ${category.name}`, `✨ ${option.name}`, ``,
      `Name: ${form.name}`, `Mobile: ${form.mobile}`,
      form.email ? `Email: ${form.email}` : null, ``,
    ];
    portal.fields.forEach(f => { if (form[f.k]) rows.push(`${f.label.replace(" · Optional", "")}: ${form[f.k]}`); });
    const j = journeySummary();
    if (j) { rows.push(``, `— Complete Your Journey —`, j); }
    return whatsappLink(rows.filter(Boolean).join("\n"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) { toast.error("Please share your name and mobile number."); return; }
    setBusy(true);
    try {
      const j = journeySummary();
      const baseMessage = form.message || "";
      const combinedMessage = [baseMessage, j && `Add-Ons: ${j}`].filter(Boolean).join(" | ") || undefined;
      const payload = {
        type: portal.enquiryType, name: form.name, mobile: form.mobile, email: form.email || undefined,
        destination: category.name, hotel: option.name,
        ...Object.fromEntries(portal.fields.map(f => [f.k, form[f.k] || undefined])),
        message: combinedMessage,
      };
      const saved = await submitEnquiry(payload);
      const url = buildWhatsApp();
      window.open(url, "_blank", "noreferrer");
      setDone({ id: saved.id, url });
      toast.success("Received. Namaste 🙏");
    } catch { toast.error("Something interrupted our line. Please WhatsApp us instead."); }
    finally { setBusy(false); }
  };

  if (done) return (
    <section className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="portal-success">
      <div className="border border-gold/40 bg-surface p-10 md:p-16 text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-gold/10 border border-gold flex items-center justify-center text-gold"><Check size={22} strokeWidth={2} /></div>
        <div className="overline no-line mt-8">Namaste</div>
        <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight">Your enquiry for <span className="italic text-gold">{option.name}</span> has been received.</h2>
        <p className="mt-6 text-ink-soft text-[15px] leading-relaxed max-w-lg mx-auto">A member of our team will personally reach you on <b>{form.mobile}</b> within 10 minutes. We have also opened WhatsApp so you can send us a message directly.</p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <a href={done.url} target="_blank" rel="noreferrer" className="btn-saffron" data-testid="portal-open-whatsapp"><MessageCircle size={14} /> Continue on WhatsApp</a>
          <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="btn-ghost" data-testid="portal-call-now"><Phone size={13} /> Call Now</a>
          <Link to="/" className="btn-ghost" data-testid="portal-back-home">Back to Home</Link>
        </div>
        <div className="mt-12 pt-8 border-t border-kc text-[11px] uppercase tracking-[0.28em] text-ink-mute">Enquiry ID · {done.id.slice(0, 8)}</div>
      </div>
    </section>
  );

  return (
    <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="portal-step3-form">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-ink-soft hover:text-gold transition-colors mb-10" data-testid="back-to-step2">
        <ArrowLeft size={13} /> Change {portal.step2SelectedIntroLabel}
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4">
          <div className="bg-black-lux text-[#F8F6F2] p-8 md:p-10 border border-gold/25 lg:sticky lg:top-28">
            <div className="overline no-line text-gold">Your Selection</div>
            <div className="mt-8 pb-6 border-b border-[#D4AF37]/25">
              <div className="text-[10px] uppercase tracking-[0.28em] text-gold">{portal.step1CrumbLabel.replace(/s$/, "")}</div>
              <div className="font-serif-display text-2xl mt-2">{category.name}</div>
              <div className="text-[12px] text-[#F8F6F2]/60 mt-1">{category.subtitle}</div>
            </div>
            <div className="mt-6 pb-6 border-b border-[#D4AF37]/25">
              <div className="text-[10px] uppercase tracking-[0.28em] text-gold">{portal.step2SelectedIntroLabel}</div>
              <div className="font-serif-display text-xl mt-2 leading-tight">{option.name}</div>
              {option.note && <div className="text-[12px] text-[#F8F6F2]/60 mt-1">{option.note}</div>}
            </div>

            {category.isHelicopter && (
              <div className="mt-6 p-4 border border-gold/40 bg-gold/5" data-testid="helicopter-note">
                <div className="flex items-center gap-2 text-gold">
                  <CloudSun size={14} strokeWidth={1.4} />
                  <div className="text-[10px] uppercase tracking-[0.28em]">Important</div>
                </div>
                <p className="mt-2 text-[12px] text-[#F8F6F2]/75 leading-[1.6]">
                  Subject to weather & IRCTC slot availability. Bookings are confirmed only once your slot is secured with an authorised operator. Our team confirms availability within 30 minutes.
                </p>
              </div>
            )}

            {category.startingFrom && !category.isHelicopter && (
              <div className="mt-6 p-4 border border-[#D4AF37]/25 flex items-baseline justify-between" data-testid="starting-price">
                <span className="text-[10px] uppercase tracking-[0.28em] text-gold">Starting From</span>
                <span className="font-serif-display text-lg text-[#F8F6F2]">{category.startingFrom}</span>
              </div>
            )}
            <div className="mt-8 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Every enquiry is routed to</div>
              <div className="space-y-2 text-[12px] text-[#F8F6F2]/75">
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-gold rounded-full" /> WhatsApp — instant hand-off</div>
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-gold rounded-full" /> Email — team notification</div>
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-gold rounded-full" /> Admin dashboard — tracked</div>
              </div>
            </div>
            <div className="mt-10 pt-6 border-t border-[#D4AF37]/25">
              <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Prefer to talk?</div>
              <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="font-serif-display text-xl mt-2 block hover:text-gold transition-colors">{BRAND.phone}</a>
              <div className="text-[11px] text-[#F8F6F2]/50 mt-1">Answered personally · 7 AM – 11 PM</div>
            </div>
          </div>
        </aside>
        <form onSubmit={submit} className="lg:col-span-8 bg-surface border border-kc p-8 md:p-12" data-testid="portal-form-fields">
          <div className="overline no-line">Guest Details</div>
          <h2 className="font-serif-display text-3xl md:text-4xl mt-4 leading-tight">Begin Your Journey</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            <FieldInput label="Name" value={form.name} onChange={v => upd("name", v)} placeholder="Your full name" required testid="portal-name" />
            <FieldInput label="Mobile" value={form.mobile} onChange={v => upd("mobile", v)} placeholder="+91 …" required testid="portal-mobile" />
            <FieldInput label="Email · Optional" value={form.email} onChange={v => upd("email", v)} placeholder="you@example.com" col={2} testid="portal-email" />
            {portal.fields.map(f => {
              const testid = `portal-${f.k}`;
              if (f.type === "select") return <FieldSelect key={f.k} label={f.label} value={form[f.k] || f.options[0]} onChange={v => upd(f.k, v)} options={f.options} col={f.col} testid={testid} />;
              if (f.type === "textarea") return <FieldInput key={f.k} textarea label={f.label} value={form[f.k] || ""} onChange={v => upd(f.k, v)} placeholder={f.placeholder} col={f.col} testid={testid} />;
              return <FieldInput key={f.k} type={f.type} label={f.label} value={form[f.k] || ""} onChange={v => upd(f.k, v)} placeholder={f.placeholder} col={f.col} testid={testid} />;
            })}
          </div>

          {portal.key === "stay" && <JourneyAddons selections={journey} setSelections={setJourney} />}

          <button type="submit" disabled={busy} className="mt-10 btn-saffron w-full md:w-auto justify-center" data-testid="portal-submit">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <>{portal.submitLabel} <ArrowUpRight size={14} /></>}
          </button>
          <p className="mt-5 text-[10px] uppercase tracking-[0.24em] text-ink-mute">By sharing your details, you consent to be contacted by THE KC INDIA on WhatsApp, phone and email.</p>
        </form>
      </div>
    </section>
  );
};

// ============================================================================
// FIELD PRIMITIVES
// ============================================================================
const FieldInput = ({ dark, label, value, onChange, placeholder, type = "text", required, col = 1, testid, textarea }) => {
  const fieldCls = dark
    ? "w-full bg-transparent border border-[#D4AF37]/25 px-4 py-3.5 text-[13.5px] text-[#F8F6F2] placeholder:text-[#F8F6F2]/40 focus:outline-none focus:border-gold transition-colors"
    : "w-full bg-transparent border border-kc px-4 py-3.5 text-[13.5px] text-ink placeholder:text-ink-mute focus:outline-none focus:border-gold transition-colors";
  const labelCls = "text-[10px] uppercase tracking-[0.28em] text-gold mb-2 block";
  return (
    <div className={col === 2 ? "md:col-span-2" : ""}>
      <span className={labelCls}>{label}</span>
      {textarea
        ? <textarea rows={3} className={fieldCls} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} data-testid={testid} />
        : <input type={type} className={fieldCls} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} required={required} data-testid={testid} />}
    </div>
  );
};

const FieldSelect = ({ label, value, onChange, options, col = 1, testid }) => (
  <div className={col === 2 ? "md:col-span-2" : ""}>
    <span className="text-[10px] uppercase tracking-[0.28em] text-gold mb-2 block">{label}</span>
    <select className="w-full bg-transparent border border-kc px-4 py-3.5 text-[13.5px] text-ink focus:outline-none focus:border-gold transition-colors" value={value} onChange={e => onChange(e.target.value)} data-testid={testid}>
      {options.map(o => <option key={o}>{o}</option>)}
    </select>
  </div>
);
